import type { VenueBranding } from '@lunit/shared-types';

/**
 * White-label PWA: <head> под заведение из URL (первый сегмент пути = слаг). Манифест, иконка на экран «Домой»
 * (iOS), фавиконка, цвет панели браузера и название приложения — бренд заведения из API, а не общая картинка Lunit.
 *
 * Три состояния:
 *  - корень / без слага — статический манифест и иконки Lunit (index.html + vite-plugin-pwa);
 *  - слаг есть, заведение ещё грузится — неверсионированные адреса бренда заведения: Chrome читает манифест лениво
 *    (при «Установить»), но ставить его нужно как можно раньше; цвет/название ещё неизвестны — не трогаем;
 *  - ответ API — версионированные URL из branding (manifestUrl, icons.*), themeColor, appName || название.
 *    Старый бэкенд новых полей не отдаёт — возвращаем стандартные Lunit (ранние адреса у него 404).
 */
export interface BrandHead {
  manifest: string;
  appleTouchIcon: string;
  favicon: string;
  /** undefined — не трогать (до ответа API; у старого бэкенда цвет из primaryColor ставит useBranding ядра) */
  themeColor?: string;
  /** apple-mobile-web-app-title; undefined — не трогать */
  appTitle?: string;
  /** document.title; undefined — не трогать */
  title?: string;
}

/** Стандарт Lunit — то же, что в index.html и манифесте vite-plugin-pwa. */
export const LUNIT_HEAD: Readonly<Required<BrandHead>> = Object.freeze({
  manifest: '/manifest.webmanifest',
  appleTouchIcon: '/icons/apple-touch-icon-180x180.png',
  favicon: '/icons/icon-192x192.png',
  themeColor: '#7C3AED',
  appTitle: 'Lunit',
  title: 'Lunit Loyalty — Программа лояльности',
});

interface BrandHeadInput {
  slug: string;
  /** База API: VITE_API_URL (https://api…/api/v1) или '/api/v1' (dev-прокси) */
  apiBase: string;
  /** branding из GET /venues/{slug}; нет — заведение ещё не загружено */
  branding?: Partial<VenueBranding> | null;
  venueName?: string | null;
}

// Адрес из ответа API попадает в <link href>: только https, путь своего сайта или localhost (разработка).
function safeUrl(raw: unknown): string | undefined {
  if (typeof raw !== 'string' || !raw) return undefined;
  if (raw.startsWith('/') && !raw.startsWith('//') && !raw.startsWith('/\\')) return raw;
  try {
    const u = new URL(raw);
    if (u.protocol === 'https:') return raw;
    if (u.protocol === 'http:' && (u.hostname === 'localhost' || u.hostname === '127.0.0.1')) return raw;
  } catch {
    // не URL
  }
  return undefined;
}

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

export function brandHead({ slug, apiBase, branding, venueName }: BrandHeadInput): BrandHead {
  if (!slug) return { ...LUNIT_HEAD };

  if (!branding) {
    const venue = `${apiBase.replace(/\/+$/, '')}/venues/${encodeURIComponent(slug)}`;
    return {
      manifest: `${venue}/manifest.webmanifest`,
      appleTouchIcon: `${venue}/brand/app-icon/apple180.png`,
      favicon: `${venue}/brand/app-icon/favicon32.png`,
    };
  }

  const name = branding.appName?.trim() || venueName?.trim() || undefined;
  return {
    manifest: safeUrl(branding.manifestUrl) ?? LUNIT_HEAD.manifest,
    appleTouchIcon: safeUrl(branding.icons?.appleTouch180) ?? LUNIT_HEAD.appleTouchIcon,
    favicon: safeUrl(branding.icons?.favicon32) ?? safeUrl(branding.faviconUrl) ?? LUNIT_HEAD.favicon,
    themeColor: branding.themeColor && HEX_COLOR.test(branding.themeColor) ? branding.themeColor : undefined,
    appTitle: name,
    title: name,
  };
}

function upsert(doc: Document, tag: 'link' | 'meta', key: 'rel' | 'name', value: string): Element {
  const found = doc.querySelectorAll(`${tag}[${key}="${value}"]`);
  if (found.length) return found[0];
  const el = doc.createElement(tag);
  el.setAttribute(key, value);
  doc.head.appendChild(el);
  return el;
}

/** Применяет BrandHead к документу. Фавиконка — одна PNG: прочие <link rel="icon"> (svg Lunit) убираются,
 * иначе браузер может предпочесть svg и показать Lunit вместо иконки заведения. */
export function applyBrandHead(doc: Document, head: BrandHead): void {
  upsert(doc, 'link', 'rel', 'manifest').setAttribute('href', head.manifest);
  upsert(doc, 'link', 'rel', 'apple-touch-icon').setAttribute('href', head.appleTouchIcon);

  const icons = doc.querySelectorAll('link[rel="icon"]');
  icons.forEach((el, i) => {
    if (i > 0) el.remove();
  });
  const icon = upsert(doc, 'link', 'rel', 'icon');
  icon.setAttribute('type', 'image/png');
  icon.removeAttribute('sizes');
  icon.setAttribute('href', head.favicon);

  if (head.themeColor) upsert(doc, 'meta', 'name', 'theme-color').setAttribute('content', head.themeColor);
  if (head.appTitle) upsert(doc, 'meta', 'name', 'apple-mobile-web-app-title').setAttribute('content', head.appTitle);
  if (head.title) doc.title = head.title;
}
